import React, { useEffect, useRef, useState } from "react";
import { Upload, Trash2, ImagePlus, Play, X, Heart as HeartIcon, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { mediaThumbUrl, mediaFileUrl } from "@/lib/mediaUrls";
import { toast } from "sonner";

const PAGE_SIZE = 12;

const MediaContent = ({ item }) => {
  if (item.media_type === "video") {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-rose-950/60 to-[#0F0407]">
        <Play className="w-14 h-14 text-rose-200/90" fill="currentColor" />
      </div>
    );
  }
  return (
    <img
      src={mediaThumbUrl(item.id, 600)}
      alt={item.caption || item.original_filename}
      loading="lazy"
      decoding="async"
      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
    />
  );
};

const MediaThumb = ({ item, onClick, onDelete, onFavorite }) => {
  return (
    <div className="group relative overflow-hidden rounded-xl polaroid">
      <div className="relative aspect-[4/5] bg-[#1A0A0E] cursor-pointer" onClick={onClick}>
        <MediaContent item={item} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
        {item.caption && (
          <div className="absolute bottom-2 left-3 right-3 font-accent text-rose-100 text-xl opacity-0 group-hover:opacity-100 transition-opacity drop-shadow">
            {item.caption}
          </div>
        )}
      </div>

      <button
        data-testid={`media-favorite-${item.id}`}
        onClick={(e) => {
          e.stopPropagation();
          onFavorite(item.id);
        }}
        className={`absolute top-2 left-2 w-8 h-8 rounded-full backdrop-blur-sm flex items-center justify-center transition ${
          item.is_favorite
            ? "bg-rose-600/80 text-white opacity-100"
            : "bg-black/60 text-rose-200 opacity-0 group-hover:opacity-100 hover:bg-rose-600/80 hover:text-white"
        }`}
        aria-label="Favori"
      >
        <HeartIcon className="w-4 h-4" fill={item.is_favorite ? "currentColor" : "none"} />
      </button>

      <button
        data-testid={`media-delete-${item.id}`}
        onClick={(e) => {
          e.stopPropagation();
          onDelete(item.id);
        }}
        className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/70 hover:bg-rose-600 text-rose-100 flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
        aria-label="Supprimer"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
};

const LightboxContent = ({ item }) => {
  if (item.media_type === "video") {
    return <video src={mediaFileUrl(item.id)} controls autoPlay className="max-h-[80vh] rounded-xl shadow-2xl" />;
  }
  return (
    <img
      src={mediaFileUrl(item.id)}
      alt={item.caption}
      className="max-h-[80vh] rounded-xl shadow-2xl object-contain"
    />
  );
};

const Lightbox = ({ item, onClose }) => {
  if (!item) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={onClose}>
      <button
        className="absolute top-6 right-6 w-10 h-10 rounded-full bg-black/60 hover:bg-rose-600 text-rose-100 flex items-center justify-center"
        onClick={onClose}
        data-testid="lightbox-close"
      >
        <X className="w-5 h-5" />
      </button>
      <div className="max-w-5xl max-h-[85vh] w-full flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
        <LightboxContent item={item} />
        {item.caption && (
          <p className="font-accent text-2xl text-rose-200 mt-6 text-center">{item.caption}</p>
        )}
      </div>
    </div>
  );
};

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [active, setActive] = useState(null);

  const load = async (reset = false) => {
    setLoading(true);
    try {
      const nextSkip = reset ? 0 : skip;
      const { data } = await api.get("/media", {
        params: { skip: nextSkip, limit: PAGE_SIZE, favorites_only: favoritesOnly },
      });
      setTotal(data.total);
      setItems(reset ? data.items : [...items, ...data.items]);
      setSkip(nextSkip + data.items.length);
    } catch (e) {
      console.error("Failed to load media list", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favoritesOnly]);

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("caption", caption);
      await api.post("/media/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Souvenir ajouté", { description: "Pour toujours dans notre album ♡" });
      setFile(null);
      setCaption("");
      setOpen(false);
      load(true);
    } catch (e) {
      toast.error("Échec du téléversement", { description: e?.response?.data?.detail || "Réessaie" });
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/media/${id}`);
      toast.success("Souvenir retiré");
      setItems((s) => s.filter((x) => x.id !== id));
      setTotal((t) => Math.max(0, t - 1));
    } catch (e) {
      toast.error("Suppression échouée");
    }
  };

  const favorite = async (id) => {
    try {
      const { data } = await api.patch(`/media/${id}/favorite`);
      setItems((s) =>
        favoritesOnly && !data.is_favorite
          ? s.filter((x) => x.id !== id)
          : s.map((x) => (x.id === id ? { ...x, is_favorite: data.is_favorite } : x))
      );
    } catch {
      toast.error("Action échouée");
    }
  };

  const canLoadMore = items.length < total;

  return (
    <section id="galerie" data-testid="gallery-section" className="px-6 md:px-12 lg:px-24 py-20 lg:py-28 relative">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <p className="font-accent text-3xl text-rose-300">nos souvenirs</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-rose-50 mt-2">Galerie de nous</h2>
            <p className="text-rose-200/70 mt-3 max-w-xl">Chaque photo, chaque vidéo, un instant figé pour l'éternité.</p>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <Tabs value={favoritesOnly ? "fav" : "all"} onValueChange={(v) => setFavoritesOnly(v === "fav")}>
              <TabsList className="bg-[#1A0A0E] border border-rose-900/40">
                <TabsTrigger data-testid="gallery-tab-all" value="all" className="data-[state=active]:bg-rose-900/40 data-[state=active]:text-rose-50 text-rose-200/70">
                  Tout
                </TabsTrigger>
                <TabsTrigger data-testid="gallery-tab-fav" value="fav" className="data-[state=active]:bg-rose-900/40 data-[state=active]:text-rose-50 text-rose-200/70">
                  <Sparkles className="w-3.5 h-3.5 mr-1" /> Favoris
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button
                  data-testid="open-upload-dialog"
                  className="bg-rose-600 hover:bg-rose-500 text-white rounded-full px-6 h-12 hover:shadow-[0_0_25px_rgba(230,57,70,0.4)]"
                >
                  <ImagePlus className="w-4 h-4 mr-2" />
                  Ajouter
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#1A0A0E] border-rose-900/40 text-rose-50">
                <DialogHeader>
                  <DialogTitle className="font-serif text-2xl text-rose-50">Téléverser un souvenir</DialogTitle>
                  <DialogDescription className="text-rose-200/70">
                    Photo ou vidéo — ajoute une petite note d'amour.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-rose-200/80">Fichier</Label>
                    <Input
                      data-testid="upload-file-input"
                      type="file"
                      accept="image/*,video/*"
                      onChange={(e) => setFile(e.target.files?.[0] || null)}
                      className="bg-[#0F0407] border-rose-900/40"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-rose-200/80">Légende (optionnelle)</Label>
                    <Textarea
                      data-testid="upload-caption-input"
                      value={caption}
                      onChange={(e) => setCaption(e.target.value)}
                      placeholder="Ce jour-là, tu m'as souri et le monde s'est arrêté..."
                      className="bg-[#0F0407] border-rose-900/40"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    data-testid="upload-submit-button"
                    onClick={upload}
                    disabled={!file || uploading}
                    className="bg-rose-600 hover:bg-rose-500 text-white rounded-full"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploading ? "Envoi..." : "Sauvegarder"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {items.length === 0 && !loading ? (
          <div className="romantic-card rounded-2xl p-16 text-center">
            <HeartIcon className="w-10 h-10 text-rose-400 mx-auto" fill="currentColor" />
            <p className="font-accent text-3xl text-rose-300 mt-4">
              {favoritesOnly ? "Aucun favori pour l'instant" : "Bientôt rempli d'amour"}
            </p>
            <p className="text-rose-200/60 mt-2">
              {favoritesOnly ? "Marque tes photos préférées d'un cœur." : "Téléverse ta première photo pour commencer notre album."}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {items.map((it) => (
                <MediaThumb
                  key={it.id}
                  item={it}
                  onClick={() => setActive(it)}
                  onDelete={remove}
                  onFavorite={favorite}
                />
              ))}
            </div>
            {canLoadMore && (
              <div className="flex justify-center mt-12">
                <Button
                  data-testid="gallery-load-more"
                  onClick={() => load(false)}
                  disabled={loading}
                  variant="outline"
                  className="border-rose-700/60 text-rose-100 hover:bg-rose-900/30 rounded-full h-12 px-8"
                >
                  {loading ? "Chargement..." : `Charger plus (${total - items.length} restants)`}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
      <Lightbox item={active} onClose={() => setActive(null)} />
    </section>
  );
}
